interface PdfViewport {
	width: number;
	height: number;
}

interface PdfRenderTask {
	promise: Promise<void>;
	cancel: () => void;
}

interface PdfPage {
	getViewport: (options: { scale: number }) => PdfViewport;
	render: (options: { canvasContext: CanvasRenderingContext2D; viewport: PdfViewport }) => PdfRenderTask;
}

interface PdfDocument {
	numPages: number;
	getPage: (number: number) => Promise<PdfPage>;
}

interface PdfLib {
	GlobalWorkerOptions: { workerSrc: string };
	getDocument: (options: Record<string, unknown>) => { promise: Promise<PdfDocument> };
}

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];

let lib: Promise<PdfLib> | null = null;

function loadLib(url: string, worker: string): Promise<PdfLib> {
	lib ??= (import(/* webpackIgnore: true */ url) as Promise<PdfLib>).then((module) => {
		module.GlobalWorkerOptions.workerSrc = worker;

		return module;
	});

	return lib;
}

function initViewer(root: HTMLElement): void {
	const viewer = root.querySelector<HTMLElement>('[data-pdf-viewer]');
	const stage = root.querySelector<HTMLElement>('[data-pdf-stage]');
	const sheet = root.querySelector<HTMLElement>('[data-pdf-sheet]');
	const src = root.dataset.src;

	if (!viewer || !stage || !sheet || !src) {
		return;
	}

	const pageLabel = root.querySelector<HTMLElement>('[data-pdf-page]');
	const totalLabel = root.querySelector<HTMLElement>('[data-pdf-total]');
	const levelLabel = root.querySelector<HTMLElement>('[data-pdf-level]');
	const prevButtons = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-pdf-prev]'));
	const nextButtons = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-pdf-next]'));
	const zoomButtons = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-pdf-zoom]'));
	const fullButton = root.querySelector<HTMLButtonElement>('[data-pdf-full]');

	let doc: Promise<PdfDocument> | null = null;
	let total = Number(totalLabel?.textContent) || 0;
	let current = 1;
	let zoom = 2;
	let task: PdfRenderTask | null = null;
	let ticket = 0;

	const update = (): void => {
		if (pageLabel) {
			pageLabel.textContent = String(current);
		}

		if (totalLabel && total) {
			totalLabel.textContent = String(total);
		}

		if (levelLabel) {
			levelLabel.textContent = `${Math.round(ZOOMS[zoom] * 100)}%`;
		}

		prevButtons.forEach((button) => {
			button.disabled = current <= 1;
		});
		nextButtons.forEach((button) => {
			button.disabled = total > 0 && current >= total;
		});
		zoomButtons.forEach((button) => {
			const step = Number(button.dataset.pdfZoom);

			button.disabled = step < 0 ? zoom <= 0 : zoom >= ZOOMS.length - 1;
		});

		viewer.classList.toggle('is-zoomed', ZOOMS[zoom] > 1);
	};

	const open = (): Promise<PdfDocument> => {
		doc ??= loadLib(root.dataset.lib ?? '', root.dataset.worker ?? '')
			.then((pdf) =>
				pdf.getDocument({
					url: src,
					disableAutoFetch: true,
					disableStream: true,
					rangeChunkSize: 262144,
					isEvalSupported: false,
				}).promise
			)
			.then((loaded) => {
				total = loaded.numPages;
				update();

				return loaded;
			})
			.catch((error: unknown) => {
				root.classList.add('is-failed');
				throw error;
			});

		return doc;
	};

	// Draws into a fresh canvas and swaps it in, so the old page stays until the new one is ready.
	const render = async (): Promise<void> => {
		const mine = ++ticket;
		const loaded = await open();
		const page = await loaded.getPage(current);

		if (mine !== ticket) {
			return;
		}

		const base = page.getViewport({ scale: 1 });
		const fit = Math.min(stage.clientWidth / base.width, stage.clientHeight / base.height);
		const scale = fit * ZOOMS[zoom];
		const ratio = Math.min(window.devicePixelRatio || 1, 2);
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');

		if (!context) {
			return;
		}

		const viewport = page.getViewport({ scale: scale * ratio });

		canvas.width = Math.floor(viewport.width);
		canvas.height = Math.floor(viewport.height);
		canvas.style.width = `${Math.floor(base.width * scale)}px`;
		canvas.style.height = `${Math.floor(base.height * scale)}px`;
		canvas.className = 'armada-pdf__page';

		task?.cancel();
		task = page.render({ canvasContext: context, viewport });

		try {
			await task.promise;
		} catch {
			return;
		}

		if (mine !== ticket) {
			return;
		}

		sheet.replaceChildren(canvas);
		root.classList.add('is-ready');

		// Warm the next page so turning it is instant.
		if (current < loaded.numPages) {
			void loaded.getPage(current + 1);
		}
	};

	const go = (step: number): void => {
		const next = current + step;

		if (next < 1 || (total && next > total)) {
			return;
		}

		current = next;
		stage.scrollTo(0, 0);
		update();
		void render();
	};

	const setZoom = (step: number): void => {
		zoom = Math.max(0, Math.min(ZOOMS.length - 1, zoom + step));
		update();
		void render();
	};

	prevButtons.forEach((button) => button.addEventListener('click', () => go(-1)));
	nextButtons.forEach((button) => button.addEventListener('click', () => go(1)));
	zoomButtons.forEach((button) => button.addEventListener('click', () => setZoom(Number(button.dataset.pdfZoom))));

	viewer.addEventListener('keydown', (event) => {
		if (event.key === 'ArrowLeft') {
			go(-1);
		} else if (event.key === 'ArrowRight') {
			go(1);
		}
	});

	// Swipe between pages on touch screens; a zoomed page pans instead.
	let startX = 0;
	let startY = 0;

	stage.addEventListener(
		'touchstart',
		(event) => {
			startX = event.touches[0].clientX;
			startY = event.touches[0].clientY;
		},
		{ passive: true }
	);

	stage.addEventListener(
		'touchend',
		(event) => {
			const dx = event.changedTouches[0].clientX - startX;
			const dy = event.changedTouches[0].clientY - startY;

			if (ZOOMS[zoom] <= 1 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
				go(dx < 0 ? 1 : -1);
			}
		},
		{ passive: true }
	);

	// Drag to pan a zoomed page with the mouse.
	let drag: { x: number; y: number; left: number; top: number } | null = null;

	stage.addEventListener('pointerdown', (event) => {
		if (event.pointerType !== 'mouse' || ZOOMS[zoom] <= 1) {
			return;
		}

		drag = { x: event.clientX, y: event.clientY, left: stage.scrollLeft, top: stage.scrollTop };
		stage.setPointerCapture(event.pointerId);
		stage.classList.add('is-dragging');
	});

	stage.addEventListener('pointermove', (event) => {
		if (drag) {
			stage.scrollTo(drag.left - (event.clientX - drag.x), drag.top - (event.clientY - drag.y));
		}
	});

	const endDrag = (): void => {
		drag = null;
		stage.classList.remove('is-dragging');
	};

	stage.addEventListener('pointerup', endDrag);
	stage.addEventListener('pointercancel', endDrag);

	// Native full screen where the browser has it, a fixed overlay elsewhere (iPhone).
	const isFull = (): boolean => document.fullscreenElement === viewer || viewer.classList.contains('is-full');

	fullButton?.addEventListener('click', () => {
		if (document.fullscreenElement === viewer) {
			void document.exitFullscreen();
		} else if (viewer.classList.contains('is-full')) {
			viewer.classList.remove('is-full');
			document.documentElement.classList.remove('armada-pdf-locked');
			void render();
		} else if (viewer.requestFullscreen) {
			void viewer.requestFullscreen();
		} else {
			viewer.classList.add('is-full');
			document.documentElement.classList.add('armada-pdf-locked');
			void render();
		}
	});

	document.addEventListener('fullscreenchange', () => {
		fullButton?.setAttribute('aria-pressed', String(isFull()));

		if (doc) {
			void render();
		}
	});

	let width = stage.clientWidth;
	let resizeTimer = 0;

	new ResizeObserver(() => {
		if (!doc || stage.clientWidth === width) {
			return;
		}

		width = stage.clientWidth;
		window.clearTimeout(resizeTimer);
		resizeTimer = window.setTimeout(() => void render(), 150);
	}).observe(stage);

	// The library and the first page load only when the viewer is about to be seen.
	new IntersectionObserver(
		(entries, observer) => {
			if (entries.some((entry) => entry.isIntersecting)) {
				observer.disconnect();
				void render();
			}
		},
		{ rootMargin: '400px 0px' }
	).observe(root);

	update();
}

export default function initPdfViewers(): void {
	document.querySelectorAll<HTMLElement>('[data-armada-pdf]').forEach(initViewer);
}
